import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isReloading: boolean;
}

/**
 * DEV-11906: Error boundary for catching failed dynamic imports.
 *
 * When a landing deploy changes all Vite chunk filenames while cached HTML
 * still references the old chunks, the browser throws "Failed to fetch
 * dynamically imported module" and the page renders blank. This boundary
 * catches that error and auto-reloads once to fetch fresh HTML with the
 * correct chunk references.
 *
 * Reloads only once (via sessionStorage flag) to prevent infinite loops if
 * the chunk genuinely doesn't exist.
 */
class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> | null {
    // Detect chunk-loading errors by message pattern
    const isChunkError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError';

    if (isChunkError) {
      return { hasError: true };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError';

    if (!isChunkError) {
      // Re-throw non-chunk errors so they reach the console/monitoring
      throw error;
    }

    // Auto-reload once to fetch fresh HTML with correct chunk references
    const reloadKey = 'lc-chunk-reload-attempted';
    const hasAttemptedReload = sessionStorage.getItem(reloadKey);

    if (!hasAttemptedReload) {
      this.setState({ isReloading: true });
      sessionStorage.setItem(reloadKey, '1');
      // Small delay to show the message, then reload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    }
    // If reload was already attempted, show the error state without looping
  }

  render() {
    const { hasError, isReloading } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    if (isReloading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              margin: '0 auto 1rem',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Reloading…
            </p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      );
    }

    // Reload was already attempted but error persisted
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            color: '#111827',
          }}>
            Unable to load page
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: '0.875rem',
            marginBottom: '1.5rem',
          }}>
            The page content could not be loaded. Please try again.
          </p>
          <button
            onClick={() => {
              sessionStorage.removeItem('lc-chunk-reload-attempted');
              window.location.reload();
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkLoadErrorBoundary;
